import {
  Button,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  MultiSelectField,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { useState } from "react";
import type { PotentialOperation2 } from "src/generated/graphqlTypes";
import { disableBasedOnPotentialOperationText } from "src/routes/components/PotentialOperationsUtils";
import { DesignPackageItemSlotEditorDocument } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignPackageItemSlotEditor.generated";
import { DesignPackageSlotsDocument } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignPackageSlotsLeftNav.generated";
import { useRequestPlanPackageSlotsModal_CreateIncomingSlotsMutation } from "src/routes/libraries/design-catalog/design-package-configurator/components/RequestPlanPackageSlotsModal.generated";

type RequestPlanPackageSlotsModalProps = {
  canEdit: PotentialOperation2;
  placeholderTliId: string;
  slotName: string;
  /** The plans without a plan line or a pending request for this placeholder (i.e., `TLI.missingPlanPackages`). */
  missingPlanPackages: { id: string; name: string }[];
};

/** Requests this placeholder's slot on plans that aren't covered by it yet. */
export function RequestPlanPackageSlotsModal(props: RequestPlanPackageSlotsModalProps) {
  const { placeholderTliId, slotName, missingPlanPackages, canEdit } = props;
  const { closeModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const [planPackageIds, setPlanPackageIds] = useState<string[]>([]);

  const [createIncomingSlots] = useRequestPlanPackageSlotsModal_CreateIncomingSlotsMutation({
    refetchQueries: [DesignPackageItemSlotEditorDocument, DesignPackageSlotsDocument],
  });

  async function onRequest() {
    const { data } = await createIncomingSlots({
      variables: { input: { designPackagePlaceholderId: placeholderTliId, planPackageIds } },
    });
    const requested = data?.createPlanPackageIncomingSlots ?? [];
    triggerNotice({
      message: `Requested ${slotName} on ${requested.map((ppis) => ppis.planPackage.name).join(", ")}`,
      icon: "success",
    });
    closeModal();
  }

  return (
    <>
      <ModalHeader>Request slot on plans</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap2.$}>
          <div css={Css.sm.gray700.$}>
            Select the plans to request {slotName} on. Each request is reviewed on the plan before the slot is added.
          </div>
          <MultiSelectField
            label="Plans"
            values={planPackageIds}
            options={missingPlanPackages}
            getOptionValue={(pp) => pp.id}
            getOptionLabel={(pp) => pp.name}
            onSelect={setPlanPackageIds}
            nothingSelectedText="Select plans"
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="quaternary" onClick={closeModal} />
        <Button
          label="Send Request"
          variant="primary"
          disabled={disableBasedOnPotentialOperationText(canEdit) || planPackageIds.isEmpty}
          onClick={onRequest}
        />
      </ModalFooter>
    </>
  );
}
